import { DEFAULT_NODE_GROUP_STATUS, NodeGroupStatus } from 'k8s/components/dashboard/helpers'
import { Status } from './model'
import { BadgeVariant } from 'core/elements/badge/Badge'
import ExternalLink from 'core/components/ExternalLink'
import { ephemeralSharedStorageDoc } from 'app/docLinks'
import { VMHAStatusDetails } from 'api-client/model'

export const generateHostStatus = (hosts): Array<NodeGroupStatus> => {
  const hostStatus = JSON.parse(
    JSON.stringify({
      success: DEFAULT_NODE_GROUP_STATUS.success,
      error: DEFAULT_NODE_GROUP_STATUS.error,
      warning: DEFAULT_NODE_GROUP_STATUS.warning,
    }),
  )

  hosts?.forEach((host) => {
    if (['active', 'ok'].includes(host?.role_status)) {
      hostStatus.success.label++
    } else if (['failed', 'error'].includes(host?.role_status)) {
      hostStatus.error.label++
    } else {
      hostStatus.warning.label++
    }
  })

  return Object.values(hostStatus)
}

export const getClusterHostGroupBadgeVariant = (status: string): BadgeVariant => {
  switch (status) {
    case Status.Healthy:
      return 'success'
    case Status.Unhealthy:
      return 'error'
    case Status.Maintenance:
      return 'warning'
    case Status.Error:
      return 'error'
    case Status.Unknown:
    default:
      return 'unknown'
  }
}

const DEFAULT_TOTAL_USAGE = {
  cpuUsage: {
    current: 0,
    total: 0,
  },
  memoryUsage: {
    current: 0,
    total: 0,
  },
  storageUsage: {
    current: 0,
    total: 0,
  },
}

export const getAggregatedUsage = (hostList: any[]) => {
  const totalUsage = JSON.parse(JSON.stringify(DEFAULT_TOTAL_USAGE))
  hostList?.forEach((host) => {
    totalUsage.cpuUsage.current += host?.usage?.compute?.current || 0
    totalUsage.cpuUsage.total += host?.usage?.compute?.max || 0
    totalUsage.memoryUsage.current += host?.usage?.memory?.current || 0
    totalUsage.memoryUsage.total += host?.usage?.memory?.max || 0
    totalUsage.storageUsage.current += host?.usage?.disk?.current || 0
    totalUsage.storageUsage.total += host?.usage?.disk?.max || 0
  })

  // Round off the values to 2 decimal places
  Object.keys(totalUsage).forEach((usageType) => {
    totalUsage[usageType].current = parseFloat(totalUsage[usageType].current.toFixed(2))
    totalUsage[usageType].total = parseFloat(totalUsage[usageType].total.toFixed(2))
  })

  return totalUsage
}

export enum VMHADisplayStatus {
  PROTECTED = 'Protected',
  NOT_PROTECTED = 'Not Protected',
  DEGRADED = 'Degraded',
  DISABLED = 'Disabled',
  UNKNOWN = 'Unknown',
  LOADING = 'Loading',
}

export const VMHA_CONDITIONS = [
  {
    key: 'TwoActiveHosts',
    label: 'At least two active hosts exist in the cluster.',
  },
  // {
  //   key: 'TwoHostsPerAggregate',
  //   label: 'Host aggregates assigned via VM flavors have at least two hosts.',
  // },
  {
    key: 'SharedStorage',
    label: (
      <>
        All VMs across cluster hosts either use a block storage volume as the root disk or{' '}
        <ExternalLink url={ephemeralSharedStorageDoc}>Ephemeral Shared Storage</ExternalLink> for
        VMs using ephemeral storage for the root disk.
      </>
    ),
  },
  {
    key: 'ImageLibrary',
    label: 'At least two hosts have the image library role assigned.',
  },
  {
    key: 'PersistentStorage',
    label: 'At least two hosts have the persistent storage role assigned.',
  },
] as const

export const mapVMHAConditions = (vmhaClusters: VMHAStatusDetails) => {
  const conditions = vmhaClusters?.conditions ?? {}

  return VMHA_CONDITIONS?.map(({ key, label }) => ({
    key,
    label,
    passed: conditions[key] === 'pass',
  }))
}
